import { useState, useEffect, useContext } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field, FieldArray } from 'formik';
import toastError from '../../toasts/toastError';
import { toast } from 'react-toastify';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';

import { DeleteOutline, AttachFile } from '@mui/icons-material';

import { i18n } from '../../translate/i18n';

import api from '../../services/api';

import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const FileListSchema = Yup.object().shape({
  name: Yup.string().min(3, 'nome muito curto').required('Obrigatório'),
  message: Yup.string().required('Obrigatório'),
});

const FilesModal = ({ open, onClose, fileListId, reload }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const { user } = useContext(AuthContext);

  const [, setFiles] = useState([]);
  const [selectedFileNames, setSelectedFileNames] = useState([]);

  const initialState = {
    name: '',
    message: '',
    options: [{ name: '', path: '', mediaType: '' }],
  };

  const [fileList, setFileList] = useState(initialState);

  useEffect(() => {
    try {
      (async () => {
        if (!fileListId) return;

        const { data } = await api.get(`/files/${fileListId}`);
        setFileList(data);
      })();
    } catch (err) {
      toastError(err);
    }
  }, [fileListId, open]);

  const handleClose = () => {
    setFileList(initialState);
    setFiles([]);
    onClose();
  };

  const handleSaveFileList = async (values) => {
    const uploadFiles = async (options, filesOptions, id) => {
      const formData = new FormData();
      formData.append('fileId', id);
      formData.append('typeArch', 'fileList');
      filesOptions.forEach((fileOption, index) => {
        if (fileOption.file) {
          formData.append('files', fileOption.file);
          formData.append('mediaType', fileOption.file.type);
          formData.append('name', options[index].name);
          formData.append('id', options[index].id);
        }
      });

      try {
        const { data } = await api.post(`/files/uploadList/${id}`, formData);
        setFiles([]);
        return data;
      } catch (err) {
        toastError(err);
      }
      return null;
    };

    const fileData = { ...values, userId: user.id };

    try {
      if (fileListId) {
        const { data } = await api.put(`/files/${fileListId}`, fileData);
        if (data.options.length > 0)
          uploadFiles(data.options, values.options, fileListId);
      } else {
        const { data } = await api.post('/files', fileData);
        if (data.options.length > 0)
          uploadFiles(data.options, values.options, data.id);
      }
      toastSuccess(i18n.t('fileModal.success'));
      if (typeof reload == 'function') {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div style={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        scroll='paper'
      >
        <DialogTitle id='form-dialog-title' sx={theme.custom.dialogColor}>
          {fileListId
            ? `${i18n.t('fileModal.title.edit')}`
            : `${i18n.t('fileModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={fileList}
          enableReinitialize={true}
          validationSchema={FileListSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveFileList(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers sx={theme.custom.dialogColor}>
                <div style={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t('fileModal.form.name')}
                    name='name'
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                    sx={theme.custom.textField}
                  />
                </div>
                <br />
                <div styles={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t('fileModal.form.message')}
                    type='message'
                    multiline
                    minRows={6}
                    fullWidth
                    name='message'
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant='outlined'
                    margin='dense'
                    sx={theme.custom.textField}
                  />
                </div>
                <Typography
                  sx={{ ...theme.custom.label, marginTop: 3 }}
                  variant='subtitle1'
                >
                  {i18n.t('fileModal.form.fileOptions')}
                </Typography>

                <FieldArray name='options'>
                  {({ push, remove }) => (
                    <div style={{ width: '100%' }}>
                      {values.options &&
                        values.options.length > 0 &&
                        values.options.map((info, index) => (
                          <div style={classes.extraAttr1} key={`${index}-info`}>
                            <Field
                              as={TextField}
                              label={i18n.t('fileModal.form.extraName')}
                              name={`options[${index}].name`}
                              variant='outlined'
                              margin='dense'
                              multiline
                              fullWidth
                              minRows={1}
                              sx={theme.custom.textField}
                            />
                            <input
                              type='file'
                              onChange={(e) => {
                                const selectedFile = e.target.files[0];
                                const updatedOptions = [...values.options];
                                updatedOptions[index].file = selectedFile;
                                setFiles('options', updatedOptions);

                                const updatedFileNames = [...selectedFileNames];
                                updatedFileNames[index] = selectedFile
                                  ? selectedFile.name
                                  : '';
                                setSelectedFileNames(updatedFileNames);
                              }}
                              style={{ display: 'none' }}
                              name={`options[${index}].file`}
                              id={`file-upload-${index}`}
                            />
                            <label htmlFor={`file-upload-${index}`}>
                              <IconButton component='span'>
                                <AttachFile
                                  sx={{ color: theme.custom.colors.text }}
                                />
                              </IconButton>
                            </label>
                            <IconButton
                              size='small'
                              onClick={() => remove(index)}
                            >
                              <DeleteOutline
                                sx={{ color: theme.custom.colors.cancelButton }}
                              />
                            </IconButton>
                            {info.path ? info.path : selectedFileNames[index]}
                          </div>
                        ))}
                      <div style={classes.extraAttr}>
                        <Button
                          sx={{
                            ...theme.custom.editButton,
                            flex: 1,
                            marginTop: 8,
                          }}
                          variant='outlined'
                          onClick={() => {
                            push({ name: '', path: '' });
                            setSelectedFileNames([...selectedFileNames, '']);
                          }}
                        >
                          {`+ ${i18n.t('fileModal.buttons.fileOptions')}`}
                        </Button>
                      </div>
                    </div>
                  )}
                </FieldArray>
              </DialogContent>
              <DialogActions sx={theme.custom.dialogColor}>
                <Button
                  onClick={handleClose}
                  sx={theme.custom.cancelButton}
                  disabled={isSubmitting}
                  variant='outlined'
                >
                  {i18n.t('fileModal.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.confirmButton}
                >
                  {fileListId
                    ? `${i18n.t('fileModal.buttons.okEdit')}`
                    : `${i18n.t('fileModal.buttons.okAdd')}`}
                  {isSubmitting && (
                    <CircularProgress size={24} sx={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FilesModal;
